import React, { useCallback, useContext } from 'react';
import getConfig from 'next/config';
import cx from 'classnames';
import { getImageUrl } from '@culture/common-helpers/cultureUtils';
import parse from 'html-react-parser';
import sanitize from 'sanitize-html';

import { ImageFormatContext } from 'contexts/ImageFormatContext';
import BaseSlider from 'components/sliders/BaseSlider';
import LazyLoadImage from 'components/LazyLoad/Image';

import { MaterialPropType } from 'propTypes/common';

import styles from './styles.module.scss';

const { publicRuntimeConfig } = getConfig();

const StyledContentDetailedSlider = ({ material }) => {
	const { files } = material;
	const imageFormat = useContext(ImageFormatContext);
	const isSingleFile = files.length === 1;
	const sliderParams = {
		slidesPerView: 1,
		spaceBetween: 0,
		loop: true,
		pagination: {
			clickable: true
		}
	};

	const renderImage = (imageSrc) => (
		<img
			className={styles.StyledContentDetailedSliderSlide_Image}
			src={imageSrc}
			alt=""
		/>
	);

	const renderSlide = useCallback(
		(file) => {
			const {
				_id,
				meta: { title }
			} = file;

			const parsedTitle = title ? parse(sanitize(title)) : null;

			const previewImageSrc = getImageUrl(file, {
				format: imageFormat,
				params: {
					crop: 'fill',
					gravity: 'center',
					width: 704
				}
			});

			const lowQualityImageSrc = getImageUrl(file, {
				params: { transform: 'average' }
			});

			return (
				<div key={_id} className={styles.StyledContentDetailedSliderSlide}>
					<LazyLoadImage
						className={cx(
							styles.StyledContentDetailedSliderSlide_WrapImage,
							isSingleFile &&
								styles.StyledContentDetailedSliderSlide_WrapImage__single
						)}
						imageSrc={previewImageSrc}
						placeholder={renderImage(lowQualityImageSrc, title)}
						preloadImage
					>
						{renderImage(previewImageSrc, title)}
					</LazyLoadImage>
					{parsedTitle}
				</div>
			);
		},
		[imageFormat, isSingleFile]
	);

	if (files?.length) {
		return isSingleFile ? (
			renderSlide(files[0], true)
		) : (
			<BaseSlider
				theme="detailed"
				params={sliderParams}
				lazy={publicRuntimeConfig.features.lazy}
			>
				{files.map((file) => renderSlide(file))}
			</BaseSlider>
		);
	}

	return null;
};

StyledContentDetailedSlider.propTypes = {
	material: MaterialPropType
};

export default StyledContentDetailedSlider;
